import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import NotFound from "./pages/NotFound";
import HomeRedirect, { CityTripsRedirect } from "./pages/HomeRedirect";
import { useRef, useState, useEffect, lazy, Suspense } from "react";
import CookieBanner from "./components/Cookies/CookieBanner";
import RouteLoader from "./components/Loaders/RouteLoader";
import ChunkErrorBoundary, {
  isChunkLoadError,
  reloadOnceForChunk,
} from "./components/ChunkErrorBoundary";
import { routes } from "./routesConfig";
import { translatePath } from "./utils/routeTranslate";
import { useTranslation } from "react-i18next";
import "App.css";

// Code-splitting por ruta: cada página va en su propio chunk y el
// bundle inicial deja de cargar mapbox, jvectormap, jspdf, firebase...
// Los importers viven en un mapa para poder precargarlos en idle:
// arranque ligero + navegación instantánea (el chunk ya está bajado).
const importers = {
  Login: () => import("./pages/auth/Login"),
  SignUp: () => import("./pages/auth/SignUp"),
  ForgetPassword: () => import("./pages/auth/ForgetPassword"),
  ResetPassword: () => import("./pages/auth/ResetPassword"),
  ConfirmEmail: () => import("./pages/auth/ConfirmEmail"),
  ChatPage: () => import("./pages/messages/ChatPage"),
  JoinInvite: () => import("./pages/JoinInvite"),
  PreviewCompanions: () => import("./pages/dev/PreviewCompanions"),
  InboxPage: () => import("./pages/messages/InboxPage"),
  TriptickPrivacyPolicy: () => import("./pages/PrivacyPolicy/TriptickPrivacyPolicy"),
  ItineraryDetailPage: () => import("./components/Itinerary/ItineraryDetail/ItineraryDetailPage"),
  ItineraryEditPage: () => import("./components/Itinerary/ItineraryEdit/ItineraryEditPage"),
  MytripPage: () => import("./pages/mytrips/mytripPage"),
  CreatedTripsPage: () => import("./pages/mytrips/CreatedTripsPage"),
  InvitedTripsPage: () => import("./pages/mytrips/InvitedTripsPage"),
  SavedTripsPage: () => import("./pages/mytrips/SavedTripsPage"),
  MyprofilePage: () => import("./pages/profile/myProfilePage"),
  PdfReviewPage: () => import("./pages/pdfreview/pdfReviewPage"),
};

// Rutas más visitadas primero: se precargan antes que el resto
const PRELOAD_ORDER = [
  "ItineraryDetailPage",
  "Login",
  "SignUp",
  "MytripPage",
  "MyprofilePage",
  "CreatedTripsPage",
  "SavedTripsPage",
  "InvitedTripsPage",
  "ItineraryEditPage",
  "ForgetPassword",
  "ResetPassword",
  "TriptickPrivacyPolicy",
  "PdfReviewPage",
];

const Login = lazy(importers.Login);
const SignUp = lazy(importers.SignUp);
const ForgetPassword = lazy(importers.ForgetPassword);
const ResetPassword = lazy(importers.ResetPassword);
const ConfirmEmail = lazy(importers.ConfirmEmail);
const ChatPage = lazy(importers.ChatPage);
const JoinInvite = lazy(importers.JoinInvite);
const PreviewCompanions = lazy(importers.PreviewCompanions);
const InboxPage = lazy(importers.InboxPage);
const TriptickPrivacyPolicy = lazy(importers.TriptickPrivacyPolicy);
const ItineraryDetailPage = lazy(importers.ItineraryDetailPage);
const ItineraryEditPage = lazy(importers.ItineraryEditPage);
const MytripPage = lazy(importers.MytripPage);
const CreatedTripsPage = lazy(importers.CreatedTripsPage);
const InvitedTripsPage = lazy(importers.InvitedTripsPage);
const SavedTripsPage = lazy(importers.SavedTripsPage);
const MyprofilePage = lazy(importers.MyprofilePage);
const PdfReviewPage = lazy(importers.PdfReviewPage);
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const nextLang = i18n.language?.startsWith("es") ? "es" : "en";
    if (document?.documentElement?.lang !== nextLang) {
      document.documentElement.setAttribute("lang", nextLang);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (localStorage.getItem("token") || localStorage.getItem("googletoken")) {
      setIsLoggedIn(localStorage.getItem("token") || localStorage.getItem("googletoken"));
    }
  }, []);

  // Chunk que ya no existe tras un despliegue: el import dinámico
  // rechaza fuera del árbol de React, así que lo cazamos aquí también.
  useEffect(() => {
    const onRejection = (e) => {
      if (isChunkLoadError(e?.reason)) reloadOnceForChunk();
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
  }, []);

  // Precargar el resto de rutas cuando el navegador está ocioso:
  // la primera pintura sigue ligera y navegar es instantáneo.
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      PRELOAD_ORDER.forEach((key, i) => {
        setTimeout(() => {
          if (!cancelled) importers[key]().catch(() => {});
        }, i * 200);
      });
    };
    const idleId =
      "requestIdleCallback" in window
        ? window.requestIdleCallback(warm, { timeout: 4000 })
        : setTimeout(warm, 2000);
    return () => {
      cancelled = true;
      if ("cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      else clearTimeout(idleId);
    };
  }, []);

  // "Copiar este viaje" iniciado sin sesión: completar la copia al entrar
  const copyingRef = useRef(false);
  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("googletoken");
    const pending = localStorage.getItem("pendingCopyTrip");
    if (!token || !pending || copyingRef.current) return;
    copyingRef.current = true;
    localStorage.removeItem("pendingCopyTrip");
    import("./api").then(({ BASEURL }) =>
      fetch(`${BASEURL}/itineraries/${pending}/duplicate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.json())
        .then((data) => {
          if (data?._id) {
            const langPrefix = i18n.language?.startsWith("es") ? "es" : "en";
            navigate(`/${langPrefix}/itinerary/${data._id}`, { replace: true });
          }
        })
        .catch((e) => console.warn("No se pudo completar la copia:", e))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const fixingRef = useRef(false);

  useEffect(() => {
    if (fixingRef.current) return;

    const lang = i18n.language;
    const path = location.pathname;
    const parts = path.split("/").filter(Boolean);
    const currentLang = parts[0];

    // 🚫 1️⃣ Evitar redirección si la ruta es "neutral" (sin idioma)
    // Esto impide que /pdf-itinerary/... o /health, /robots.txt, etc. sean redirigidas.
    const neutralPrefixes = ["/pdf-itinerary", "/health", "/robots.txt", "/sitemap.xml"];
    if (neutralPrefixes.some((prefix) => path.startsWith(prefix))) {
      return; // ← salir completamente del hook
    }

    // 🌍 2️⃣ Si no tiene prefijo de idioma (ni /es ni /en), añadir el idioma actual
    if (!["es", "en"].includes(currentLang)) {
      fixingRef.current = true;
      navigate(`/${lang}${path === "/" ? "" : path}${location.search}${location.hash}`, { replace: true });
      fixingRef.current = false;
      return;
    }

    // 🔁 3️⃣ Traducir la ruta al idioma activo conservando sus parámetros.
    // Antes se reconstruía como `/${lang}/${slug}` con solo el segundo tramo,
    // así que cambiar de idioma dentro de un viaje o de un chat tiraba el id
    // y acababas en la home. Se compara por el slug, no por el prefijo: el
    // selector de idioma cambia el prefijo pero deja el slug del otro idioma.
    const target = translatePath(path, lang);
    if (!target || target === path) return;

    fixingRef.current = true;
    navigate(`${target}${location.search}${location.hash}`, { replace: true });
    fixingRef.current = false;
  }, [i18n.language, location.pathname, location.search, location.hash, navigate]);




  return (
    <>
      <CookieBanner />
      <ChunkErrorBoundary>
      <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="*" element={<NotFound />} />
        <Route
          exact
          path="/pdf-itinerary/:id"
          element={
            <PdfReviewPage />
          }
        />
        <Route
          exact
          path={routes[lang].editItinerary}
          element={
            <ItineraryEditPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].itinerary}
          element={
            <ItineraryDetailPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        {routes[lang].itinerarySlug && (
          <Route
            exact
            path={routes[lang].itinerarySlug}
            element={<ItineraryDetailPage setIsLoggedIn={setIsLoggedIn} />}
          />
        )}
        <Route
          exact
          path={routes[lang].itineraries}
          element={<HomeRedirect to={routes[lang].itineraries} />}
        />
        {process.env.NODE_ENV === "development" && (
          <Route path="/es/__preview/companions" element={<PreviewCompanions />} />
        )}
        <Route path="/es/unirse/:token" element={<JoinInvite setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/en/join/:token" element={<JoinInvite setIsLoggedIn={setIsLoggedIn} />} />
        <Route path="/" element={<HomeRedirect lang={lang} />} />
        <Route path="/es" element={<HomeRedirect lang="es" />} />
        <Route path="/en" element={<HomeRedirect lang="en" />} />
        <Route
          exact
          path={routes[lang].profile}
          element={
            <MyprofilePage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].login}
          element={
            <Login isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
          }
        />
        <Route exact path={routes[lang].privacyPolicy} element={<TriptickPrivacyPolicy />} />
        <Route
          exact
          path={routes[lang].signup}
          element={
            <SignUp isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
          }
        />
        <Route
          exact
          path={routes[lang].forgetPassword}
          element={<ForgetPassword setIsLoggedIn={setIsLoggedIn} />}
        />
        <Route exact path={routes[lang].resetPassword} element={<ResetPassword />} />
        <Route
          exact
          path={routes[lang].confirmEmail}
          element={<ConfirmEmail setIsLoggedIn={setIsLoggedIn} />}
        />
        <Route
          exact
          path={routes[lang].inbox}
          element={<InboxPage setIsLoggedIn={setIsLoggedIn} />}
        />
        <Route
          exact
          path={routes[lang].messages}
          element={<ChatPage setIsLoggedIn={setIsLoggedIn} />}
        />
        <Route
          exact
          path={routes[lang].myTrips}
          element={
            <MytripPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].InvitedTrips}
          element={
            <InvitedTripsPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].SavedTrips}
          element={
            <SavedTripsPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].FromTheCommunity}
          element={<HomeRedirect to={routes[lang].FromTheCommunity} />}
        />
        <Route
          exact
          path={routes[lang].cityTrips}
          element={<CityTripsRedirect />}
        />
        <Route
          exact
          path={routes[lang].CreatedTrips}
          element={
            <CreatedTripsPage setIsLoggedIn={setIsLoggedIn}/>
          }
        />
        <Route
          exact
          path={routes[lang].properties}
          element={<HomeRedirect to={routes[lang].properties} />}
        />

      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    </>
  );
}

export default App;
